import React from 'react'
import styled, { CSSProp } from 'styled-components'
import {
  AvatarProps,
  StyledAvatarProps,
  SizeVariant,
  BorderRadiusShape,
} from './avatar.types'
import { BorderRadius } from '../../theme/theme'
import { styledVariant } from '../../util'
import { Theme } from '../../theme'
import { Tooltip } from '../tooltip'

export const getBorderRadius = (
  shape: BorderRadiusShape,
  theme: Theme,
  size: SizeVariant
) => {
  switch (shape) {
    case 'oval':
      return '50%'
    case 'square':
      return theme.borderRadii[BorderRadiiMap[size]]
    default:
      return theme.borderRadii[BorderRadiiMap[size]]
  }
}

const BorderSizeMap: Record<SizeVariant, string> = {
  xsmall: '.0625rem',
  small: '.0625rem',
  medium: '.0625rem',
  large: '.125rem',
  xlarge: '.125rem',
}

const BorderRadiiMap: Record<SizeVariant, BorderRadius> = {
  xsmall: 'small',
  small: 'small',
  medium: 'medium',
  large: 'medium',
  xlarge: 'medium',
}

const sizes: Record<
  SizeVariant,
  (obj: {
    theme: Theme
    $size: SizeVariant
    $shape: BorderRadiusShape
  }) => CSSProp
> = {
  xsmall: ({ theme, $size, $shape }) => `
    width: ${theme.sizes[4]};
    min-width: ${theme.sizes[4]};
    height: ${theme.sizes[4]};
    min-height: ${theme.sizes[4]};
    font-size: ${theme.fontSizes.caption};
    border-radius: ${theme.borderRadii[BorderRadiiMap[$size]]};
    `,
  small: ({ theme, $size, $shape }) => `
    width: ${theme.sizes[6]};
    min-width: ${theme.sizes[6]};
    height: ${theme.sizes[6]};
    min-height: ${theme.sizes[6]};
    font-size: ${theme.fontSizes.bodySmall};
    border-radius: ${getBorderRadius($shape, theme, $size)};
    `,
  medium: ({ theme, $size, $shape }) => `
    width: ${theme.sizes[12]};
    min-width: ${theme.sizes[12]};
    height: ${theme.sizes[12]};
    min-height: ${theme.sizes[12]};
    font-size: ${theme.fontSizes.body};
    border-radius: ${getBorderRadius($shape, theme, $size)};
    `,
  large: ({ theme, $size, $shape }) => `
    width: ${theme.sizes[16]};
    min-width: ${theme.sizes[16]};
    height: ${theme.sizes[16]};
    min-height: ${theme.sizes[16]};
    font-size: ${theme.fontSizes.h2};
    border-radius: ${getBorderRadius($shape, theme, $size)};
    `,
  xlarge: ({ theme, $size, $shape }) => `
    width: ${theme.sizes[24]};
    min-width: ${theme.sizes[24]};
    height: ${theme.sizes[24]};
    min-height: ${theme.sizes[24]};
    font-size: ${theme.fontSizes.xl};
    border-radius: ${getBorderRadius($shape, theme, $size)};
  `,
}

export const StyledAvatar = styled.div<StyledAvatarProps>`
  ${styledVariant('$size', sizes)};

  ${({
    theme,
    $size,
    $image,
    $border,
    $backgroundSizeMode,
    $transparent,
    $shape,
  }) =>
    `
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;

    ${$image ? `background-image: url(${$image})` : ''};
    background-size: ${$backgroundSizeMode};
    background-color:  ${$transparent ? 'transparent' : theme.colors.gray80};
    background-position: center;
    background-repeat: no-repeat;


    ${
      $border &&
      $image &&
      $size &&
      $shape &&
      `
    ${
      $shape === 'oval'
        ? `border: 2px solid ${theme.colors.gray90};`
        : `border: ${BorderSizeMap[$size]} solid ${theme.colors.white};
    outline: ${BorderSizeMap[$size]} solid white;
    outline-offset: -${BorderSizeMap[$size]};`
    }
    border-radius: ${getBorderRadius($shape, theme, $size)};
    `
    }
    `}
`

export const StyledBadge = styled.div<{
  $badge: AvatarProps['badge']
}>`
  ${({ theme, $badge }) => `
    width: 24px;
    height: 24px;
    position: absolute;
    bottom: -6px;
    right: -6px;
    z-index: 1;

    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;

    background-image: url(${$badge});
    background-color:  ${theme.colors.white};
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;

    border-radius: 50%;
    border: 2px solid ${theme.colors.gray90};
    
    pointer-events: auto;
  `}
`

export const StyledAvatarWrapper = styled.div`
  position: relative;
  display: inline-block;
`

export const StyledInitial = styled.span`
  ${({ theme }) =>
    `
    color: ${theme.colors.gray800};
    font-weight: ${theme.fontWeights.medium};
    `}
`

export const Avatar = ({
  image,
  size = 'medium',
  alt,
  border,
  backgroundSizeMode = 'cover',
  badge,
  badgeTooltip,
  tooltip,
  customInitials,
  transparent,
  shape = 'square',
  dataTestId = 'avatar',
  ariaLabel,
  ...props
}: AvatarProps) => {
  const renderBadge = () => {
    const badgeElement = (
      <StyledBadge $badge={badge} data-testid="avatar-badge" />
    )

    if (badgeTooltip && badgeTooltip.trim()) {
      return (
        <Tooltip content={badgeTooltip} placement="top">
          {badgeElement}
        </Tooltip>
      )
    }

    return badgeElement
  }

  const avatarContent = (
    <StyledAvatar
      $image={image}
      $size={size}
      $border={border}
      $backgroundSizeMode={backgroundSizeMode}
      $transparent={transparent}
      $shape={shape}
      data-testid={dataTestId}
      aria-label={ariaLabel ?? alt}
      {...props}
    >
      {!image && (
        <StyledInitial>
          {customInitials || alt?.charAt(0).toUpperCase()}
        </StyledInitial>
      )}
    </StyledAvatar>
  )

  if (badge && tooltip && tooltip.trim()) {
    return (
      <StyledAvatarWrapper>
        <Tooltip content={tooltip} placement="top">
          {avatarContent}
        </Tooltip>
        {renderBadge()}
      </StyledAvatarWrapper>
    )
  }

  if (badge) {
    return (
      <StyledAvatarWrapper>
        {avatarContent}
        {renderBadge()}
      </StyledAvatarWrapper>
    )
  }

  if (tooltip && tooltip.trim()) {
    return (
      <Tooltip content={tooltip} placement="top">
        {avatarContent}
      </Tooltip>
    )
  }

  return avatarContent
}
